import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { GridSegmentTab } from "src/app/_models/common/grid/grid-config";
import { ButtonComponent } from "../button/button.component";
import { ButtonConfig } from "../button/button.modal";

/**
 * The compact sub-tabs that sit in the LEFT slot of the shared `<grid>` toolbar — `Label (count)`
 * buttons, e.g. "Matched with receipts (2)" / "Unmatched with receipts (1)". Emits `tabSelected`
 * with the selected tab's `value`; the host decides what that filters.
 *
 * Lives in the design system rather than the claimant feature because `<grid>` renders it, and
 * `<grid>` is shared with 11 non-claimant screens.
 *
 * This is NOT the big landing cards — those are `pill-segment-card` (icon + label + amount + count
 * badge + chevron), which stays above the grid box.
 */
@Component({
  selector: "ds-segment-tabs",
  templateUrl: "./segment-tabs.component.html",
  styleUrls: ["./segment-tabs.component.scss"],
  standalone: true,
  imports: [CommonModule, ButtonComponent],
})
export class SegmentTabsComponent {
  /** Empty renders nothing, so the grid's left slot simply collapses. */
  @Input() tabs: GridSegmentTab[] = [];
  @Input() activeValue: any = null;

  @Output() tabSelected = new EventEmitter<any>();

  /**
   * Cache of the `ButtonConfig` handed to each `ds-button`, keyed by label + active state.
   *
   * `ButtonComponent` reacts in `ngOnChanges`, so returning a fresh object literal from the
   * template would re-run it on every change-detection pass. Same key → same reference → no churn.
   */
  private readonly buttonCache = new Map<string, ButtonConfig>();

  onSelect(tab: GridSegmentTab): void {
    this.activeValue = tab.value;
    this.tabSelected.emit(tab.value);
  }

  getButtonInput(tab: GridSegmentTab): ButtonConfig {
    const label = tab.count != null ? `${tab.label} (${tab.count})` : tab.label;
    const isActive = tab.value === this.activeValue;
    const cacheKey = `${label}|${isActive}`;

    let config = this.buttonCache.get(cacheKey);
    if (!config) {
      config = {
        label,
        ariaLabel: label,
        size: "sm",
        color: isActive ? "primary" : "primary-tertiary",
      };
      this.buttonCache.set(cacheKey, config);
    }
    return config;
  }
}
